import React from 'react';
import { Box, Typography, Paper, Stack, Alert, TextField, FormControlLabel, Switch, Divider } from '@mui/material';
import { Description } from '@mui/icons-material';
import { useGetCaseManagementQuery } from '../../../../services/CaseManagementService';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../app/store';
import { updateDocumentRequest } from '../../../../services/CaseManagementService/caseManageSlice';
import { DocumentRequestView } from '../../../../services/CaseManagementService/types/caseManageTypes';

const DocumentRequestTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('id');
  
  const { data: caseData } = useGetCaseManagementQuery(
    { id: caseId! }, 
    { skip: !caseId }
  );
  
  const { currentCase } = useAppSelector(state => state.caseManage);
  const documentRequest = currentCase?.documentRequest || caseData?.documentRequest;

  const handleDocumentToggle = (field: keyof DocumentRequestView, value: boolean) => {
    if (documentRequest) {
      dispatch(updateDocumentRequest({ [field]: value }));
    }
  };

  const handleNotesChange = (notes: string) => {
    dispatch(updateDocumentRequest({ note: notes }));
  };

  if (!documentRequest) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2.5, 
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 1.5
          }}
        >
          <Alert 
            severity="info" 
            sx={{ 
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              '& .MuiAlert-icon': {
                color: '#0284c7'
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              No document request data available
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
              Document requests will appear here when they are created for this case
            </Typography>
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2.5, 
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 1.5
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Description sx={{ color: '#3b82f6', fontSize: 24, mr: 1.5 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Document Request Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
              Toggle document requirements and manage additional notes
            </Typography>
          </Box>
        </Box>

        <Stack spacing={3}>
          {/* Document Type Switches */}
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2, fontWeight: 600 }}>
              Required Documents
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 1.5
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={documentRequest.historyAndPhysical || false}
                        onChange={(e) => handleDocumentToggle('historyAndPhysical', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#3b82f6',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#3b82f6',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                        History & Physical
                      </Typography>
                    }
                    sx={{ minWidth: '200px' }}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={documentRequest.imagingReport || false}
                        onChange={(e) => handleDocumentToggle('imagingReport', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#3b82f6',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#3b82f6',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                        Imaging Report
                      </Typography>
                    }
                    sx={{ minWidth: '200px' }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={documentRequest.clinicalRecord || false}
                        onChange={(e) => handleDocumentToggle('clinicalRecord', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#3b82f6',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#3b82f6',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                        Clinical Record
                      </Typography>
                    }
                    sx={{ minWidth: '200px' }}
                  />
                </Stack>

                <Divider sx={{ borderColor: '#e2e8f0' }} />

                <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={documentRequest.physicalTherapyNote || false}
                        onChange={(e) => handleDocumentToggle('physicalTherapyNote', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#3b82f6',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#3b82f6',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                        Physical Therapy Note
                      </Typography>
                    }
                    sx={{ minWidth: '200px' }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={documentRequest.pathologyReport || false}
                        onChange={(e) => handleDocumentToggle('pathologyReport', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#3b82f6',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#3b82f6',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                        Pathology Report
                      </Typography>
                    }
                    sx={{ minWidth: '200px' }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={documentRequest.psychiatricEvaluation || false}
                        onChange={(e) => handleDocumentToggle('psychiatricEvaluation', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#3b82f6',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#3b82f6',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                        Psychiatric Evaluation
                      </Typography>
                    }
                    sx={{ minWidth: '200px' }}
                  />
                </Stack>

                <Divider sx={{ borderColor: '#e2e8f0' }} />

                <Stack direction="row" spacing={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={documentRequest.otherDocument || false}
                        onChange={(e) => handleDocumentToggle('otherDocument', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#3b82f6',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#3b82f6',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1e293b' }}>
                        Other Document
                      </Typography>
                    }
                    sx={{ minWidth: '200px' }}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Box>

          {/* Notes Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 2, fontWeight: 600 }}>
              Additional Notes
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder="Add any additional notes or specific requirements about the requested documents..."
              value={documentRequest.note || ''}
              onChange={(e) => handleNotesChange(e.target.value)}
              size="small"
              InputProps={{
                sx: {
                  backgroundColor: '#ffffff',
                  '& .MuiInputBase-inputMultiline': {
                    whiteSpace: 'pre-wrap'
                  }
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                }
              }}
            />
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default DocumentRequestTab;
